import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { config } from '../../config/environment';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { 
  Trophy,
  Plus,
  Trash2,
  Save,
  Package,
  TrendingUp,
  TrendingDown,
  AlertCircle,
  DollarSign,
  Star,
  Truck,
  BarChart3,
  FileText,
  RefreshCw,
  Link2,
  ExternalLink,
  X,
  Award,
  Loader2
} from 'lucide-react';
import BSRTrendChart from './BSRTrendChart';

const API = config.apiBaseUrl;
const MOCK_TENANT_ID = 'tenant-123';

// Helper to safely format numbers
const safeToFixed = (value, decimals = 2) => {
  const num = parseFloat(value);
  if (isNaN(num) || value === null || value === undefined) {
    return decimals === 0 ? "0" : "0.00";
  }
  return num.toFixed(decimals);
};

// Format BSR number with commas
const formatBSR = (rank) => {
  if (!rank && rank !== 0) return 'N/A';
  return `#${rank.toLocaleString()}`;
};

// Fulfillment options
const FULFILLMENT_OPTIONS = ['FBA', 'FBM', 'SFP', '3PL'];

// Calculate mode (most common value)
const mode = (arr) => {
  if (!arr || arr.length === 0) return null;
  const frequency = {};
  let maxFreq = 0;
  let modeValue = arr[0];
  
  arr.forEach(item => {
    frequency[item] = (frequency[item] || 0) + 1;
    if (frequency[item] > maxFreq) {
      maxFreq = frequency[item];
      modeValue = item;
    }
  });
  
  return modeValue;
};

// ============================================================================
// BSR Section Component - Your Product's Best Seller Rank
// ============================================================================
const BSRSection = ({ productId, asin }) => {
  const [bsrData, setBsrData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch BSR data (cached from DB)
  const fetchBSR = useCallback(async (fresh = false) => {
    if (!productId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const endpoint = fresh 
        ? `${API}/bsr/product/${productId}/fetch`
        : `${API}/bsr/product/${productId}`;
      
      const response = await axios({
        method: fresh ? 'POST' : 'GET',
        url: endpoint,
        params: { tenant_id: MOCK_TENANT_ID }
      });
      
      if (response.data.success) {
        setBsrData(fresh ? response.data.bsr_record : response.data.latest_bsr);
        if (fresh) {
          toast.success('BSR refreshed from Amazon');
        }
      }
    } catch (err) {
      console.error('BSR fetch error:', err);
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to fetch BSR';
      setError(errorMsg);
      if (fresh) {
        toast.error(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  }, [productId]);

  // Load cached BSR on mount
  useEffect(() => {
    if (asin) {
      fetchBSR(false);
    }
  }, [productId, asin, fetchBSR]);

  // If no ASIN configured
  if (!asin) {
    return (
      <Card className="bg-amber-900/20 border-amber-700/50 mb-4">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-400 mt-0.5" />
            <div>
              <h4 className="text-sm font-medium text-amber-300 mb-1">BSR Tracking Not Available</h4>
              <p className="text-xs text-amber-400/80">
                Add an Amazon ASIN in the <span className="font-medium">General</span> tab to track Best Seller Rank.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-gray-800 border-gray-700 mb-4">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-xs font-medium text-gray-400 uppercase tracking-wide flex items-center gap-2">
            <Award className="w-3 h-3 text-yellow-400" />
            Your Product BSR
          </CardTitle>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-[10px] text-gray-400 font-mono">
              ASIN: {asin}
            </Badge>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => fetchBSR(true)}
              disabled={loading}
              className="h-6 text-[10px] text-blue-400 hover:text-blue-300"
              data-testid="refresh-bsr-btn"
            >
              {loading ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <>
                  <RefreshCw className="w-3 h-3 mr-1" />
                  Refresh
                </>
              )}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {error && !bsrData && (
          <div className="text-xs text-red-400 mb-2 flex items-center gap-2">
            <AlertCircle className="w-3 h-3" />
            {error}
          </div>
        )}

        {!bsrData && !loading && !error && (
          <div className="text-center py-4">
            <p className="text-sm text-gray-500 mb-2">No BSR data cached</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchBSR(true)}
              className="text-xs"
              data-testid="fetch-bsr-initial-btn"
            >
              <RefreshCw className="w-3 h-3 mr-1" />
              Fetch BSR from Amazon
            </Button>
          </div>
        )}

        {bsrData && (
          <>
            <div className="flex gap-3 flex-wrap">
              {/* Main Category BSR */}
              <div className="p-3 bg-blue-900/30 border border-blue-700/50 rounded-lg min-w-[160px]">
                <div className="flex items-center gap-1 text-[10px] text-blue-400 mb-1">
                  <Package className="w-3 h-3" />
                  {bsrData.main_category || 'Main Category'}
                </div>
                <div className="text-2xl font-bold text-white">
                  {formatBSR(bsrData.main_category_bsr)}
                </div>
              </div>

              {/* Sub-category BSRs */}
              {bsrData.sub_categories?.map((sub, idx) => (
                <div 
                  key={idx} 
                  className="p-3 bg-green-900/30 border border-green-700/50 rounded-lg min-w-[160px]"
                >
                  <div className="flex items-center gap-1 text-[10px] text-green-400 mb-1">
                    <Star className="w-3 h-3" />
                    <span className="truncate max-w-[140px]" title={sub.name}>
                      {sub.name}
                    </span>
                  </div>
                  <div className="text-2xl font-bold text-white">
                    {formatBSR(sub.bsr)}
                  </div>
                </div>
              ))}
            </div>

            {/* Last Updated */}
            <div className="mt-3 flex items-center justify-between">
              <span className="text-[10px] text-gray-500">
                Last updated: {bsrData.created_at 
                  ? new Date(bsrData.created_at).toLocaleString()
                  : bsrData.snapshot_date 
                    ? new Date(bsrData.snapshot_date).toLocaleDateString()
                    : 'Unknown'}
              </span>
              {bsrData.main_category_link && (
                <a 
                  href={bsrData.main_category_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[10px] text-blue-400 hover:text-blue-300 flex items-center gap-1"
                >
                  View on Amazon <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

const CompetitionTab = ({ 
  productId, 
  productData,
  onFieldChange = () => {}
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasData, setHasData] = useState(false);
  
  // Market Info state
  const [marketInfo, setMarketInfo] = useState({
    amazon_category: '',
    price_range_min: 0,
    price_range_max: 0,
    avg_reviews: 0
  });
  
  // Competitors state
  const [competitors, setCompetitors] = useState([]);
  
  // Notes state
  const [notes, setNotes] = useState('');
  
  // Last updated
  const [lastUpdated, setLastUpdated] = useState(null);
  
  // URL Modal state
  const [urlModalOpen, setUrlModalOpen] = useState(false);
  const [urlModalIndex, setUrlModalIndex] = useState(null);
  const [urlModalValue, setUrlModalValue] = useState('');
  
  // Get product retail price for comparison
  const productRetailPrice = parseFloat(productData?.retail_price) || 0;
  
  // Open URL modal for a competitor
  const openUrlModal = (index) => {
    setUrlModalIndex(index);
    setUrlModalValue(competitors[index]?.url || '');
    setUrlModalOpen(true);
  };
  
  // Save URL from modal
  const saveUrl = () => {
    if (urlModalIndex !== null) {
      handleCompetitorChange(urlModalIndex, 'url', urlModalValue);
    }
    setUrlModalOpen(false);
    setUrlModalIndex(null);
    setUrlModalValue('');
  };
  
  // Open URL in new tab
  const openUrlInNewTab = (url) => {
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  // Fetch existing data
  const fetchData = useCallback(async () => {
    if (!productId) return;
    
    try {
      setLoading(true);
      const response = await axios.get(`${API}/products/${productId}/competitive-analysis`, {
        params: { tenant_id: MOCK_TENANT_ID }
      });
      
      console.log('Fetched competitive analysis:', response.data);
      
      if (response.data.success && response.data.data) {
        const data = response.data.data;
        setMarketInfo(data.market_info || {
          amazon_category: '',
          price_range_min: 0,
          price_range_max: 0,
          avg_reviews: 0
        });
        
        // Ensure competitors have proper numeric values
        const loadedCompetitors = (data.competitors || []).map(c => ({
          rank: c.rank || 0,
          brand: c.brand || '',
          asin: c.asin || '',
          price: parseFloat(c.price) || 0,
          pack_size: c.pack_size || '',
          fulfillment: c.fulfillment || 'FBA',
          reviews: parseInt(c.reviews) || 0,
          bsr: parseInt(c.bsr) || 0,
          url: c.url || ''
        }));
        
        console.log('Loaded competitors:', loadedCompetitors);
        setCompetitors(loadedCompetitors);
        setNotes(data.notes || '');
        setLastUpdated(data.updated_at);
        setHasData(true);
      } else {
        setHasData(false);
      }
    } catch (error) {
      console.error('Error fetching competitive analysis:', error);
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Save data
  const handleSave = async () => {
    if (!productId) {
      toast.warning('Save the product first');
      return;
    }
    
    try {
      setSaving(true);
      
      // Ensure all competitor fields have proper types
      const cleanedCompetitors = competitors.map((c, idx) => ({
        rank: idx + 1,
        brand: c.brand || '',
        asin: c.asin || '',
        price: parseFloat(c.price) || 0,
        pack_size: c.pack_size || '',
        fulfillment: c.fulfillment || 'FBA',
        reviews: parseInt(c.reviews) || 0,
        bsr: parseInt(c.bsr) || 0,
        url: c.url || ''
      }));
      
      const payload = {
        market_info: marketInfo,
        competitors: cleanedCompetitors,
        notes
      };
      
      console.log('Saving competitive analysis:', JSON.stringify(payload, null, 2));
      
      const response = await axios.put(`${API}/products/${productId}/competitive-analysis`, payload, {
        params: { 
          tenant_id: MOCK_TENANT_ID,
          user_email: 'user@example.com'
        }
      });
      
      console.log('Save response:', response.data);
      
      // Update local state with saved data to ensure consistency
      if (response.data.success && response.data.data) {
        setCompetitors(response.data.data.competitors || []);
      }
      
      setHasData(true);
      setLastUpdated(new Date().toISOString());
      toast.success('Competitive analysis saved');
    } catch (error) {
      console.error('Error saving competitive analysis:', error);
      toast.error('Failed to save competitive analysis');
    } finally {
      setSaving(false);
    }
  };

  // Add competitor row
  const handleAddCompetitor = () => {
    if (competitors.length >= 10) {
      toast.warning('Maximum 10 competitors allowed');
      return;
    }
    
    setCompetitors([...competitors, {
      rank: competitors.length + 1,
      brand: '',
      asin: '',
      price: 0,
      pack_size: '',
      fulfillment: 'FBA',
      reviews: 0,
      bsr: 0,
      url: ''
    }]);
  };

  // Remove competitor row
  const handleRemoveCompetitor = (index) => {
    const newCompetitors = [...competitors];
    newCompetitors.splice(index, 1);
    // Re-rank
    newCompetitors.forEach((c, idx) => c.rank = idx + 1);
    setCompetitors(newCompetitors);
  };

  // Update competitor field
  const handleCompetitorChange = (index, field, value) => {
    const newCompetitors = [...competitors];
    newCompetitors[index] = {
      ...newCompetitors[index],
      [field]: value
    };
    setCompetitors(newCompetitors);
  };

  // Track which row is loading competitor data
  const [loadingCompetitorRow, setLoadingCompetitorRow] = useState(null);
  
  // Lookup competitor data by ASIN (auto-fetch on blur)
  const handleCompetitorAsinLookup = async (rowIndex, asin, forceRefresh = false) => {
    console.log('🔍 handleCompetitorAsinLookup called:', { rowIndex, asin, productId, forceRefresh });
    
    // Validate ASIN format (10 alphanumeric characters)
    const cleanAsin = (asin || '').trim().toUpperCase();
    
    if (!cleanAsin) {
      console.log('⚠️ Empty ASIN, skipping lookup');
      return;
    }
    
    if (cleanAsin.length !== 10) {
      console.log(`⚠️ ASIN "${cleanAsin}" is ${cleanAsin.length} chars, needs 10. Skipping lookup.`);
      if (cleanAsin.length > 0 && cleanAsin.length < 10) {
        toast.warning(`ASIN must be 10 characters (currently ${cleanAsin.length})`);
      }
      return;
    }
    
    // Only skip if we have COMPLETE data AND not forcing refresh
    if (!forceRefresh) {
      const existingCompetitor = competitors[rowIndex] || {};
      const hasCompleteData = existingCompetitor.brand && 
                              existingCompetitor.brand.length > 0 &&
                              existingCompetitor.bsr > 0 && 
                              existingCompetitor.asin === cleanAsin;
      
      if (hasCompleteData) {
        console.log('ℹ️ Already have complete data for this ASIN. Use refresh button to update.');
        return;
      }
    }
    
    setLoadingCompetitorRow(rowIndex);
    console.log(`📡 Fetching competitor data for ASIN: ${cleanAsin}`);
    
    try {
      const url = `${API}/bsr/competitor/lookup/${cleanAsin}`;
      console.log('📡 Full URL:', url);
      
      const response = await axios.get(url, {
        params: { tenant_id: MOCK_TENANT_ID }
      });
      
      console.log('📥 Competitor lookup response:', response.data);
      
      if (response.data.success && response.data.data) {
        const data = response.data.data;
        
        // Update the row with fetched data
        setCompetitors(prevCompetitors => {
          const updated = [...prevCompetitors];
          const current = updated[rowIndex] || {};
          
          console.log('📝 Before update:', current);
          console.log('📝 API data:', data);
          
          // Always update with fetched data (overwrite existing)
          updated[rowIndex] = {
            ...current,
            asin: cleanAsin,
            // Update brand from API (or keep existing if API returned null)
            brand: data.brand || current.brand || '',
            // Update price from API (or keep existing if API returned null/0)
            price: (data.price && data.price > 0) ? data.price : (current.price || 0),
            // Update BSR from API
            bsr: data.bsr || current.bsr || 0,
            // reviews not available from SP-API, keep existing
            reviews: current.reviews || 0,
          };
          
          console.log('✅ After update:', updated[rowIndex]);
          
          return updated;
        });
        
        // Show success with actual data fetched
        const fetchedFields = [];
        if (data.bsr) fetchedFields.push(`BSR: #${data.bsr.toLocaleString()}`);
        if (data.price) fetchedFields.push(`Price: $${data.price.toFixed(2)}`);
        if (data.brand) fetchedFields.push(`Brand: ${data.brand}`);
        
        if (fetchedFields.length > 0) {
          toast.success(`Loaded: ${fetchedFields.join(', ')}`);
        } else {
          toast.info(`Found ASIN but limited data available`);
        }
      } else if (response.data.error) {
        console.error('❌ API returned error:', response.data.error);
        toast.error(response.data.error);
      }
    } catch (error) {
      console.error('❌ Competitor lookup failed:', error);
      console.error('❌ Error details:', error.response?.data);
      const errorMsg = error.response?.data?.detail || error.response?.data?.error || 'Failed to lookup competitor';
      toast.error(errorMsg);
    } finally {
      setLoadingCompetitorRow(null);
    }
  };

  // Refresh competitor data manually (force refresh)
  const handleRefreshCompetitor = (rowIndex) => {
    const asin = competitors[rowIndex]?.asin;
    if (asin) {
      console.log('🔄 Force refreshing competitor data for:', asin);
      handleCompetitorAsinLookup(rowIndex, asin, true); // Force refresh
    }
  };

  // Calculate insights
  const insights = useMemo(() => {
    if (!competitors || competitors.length === 0) {
      return null;
    }

    // Most common pack size
    const packSizes = competitors.map(c => c.pack_size).filter(Boolean);
    const packSizeMode = mode(packSizes);
    const packSizeCount = packSizes.filter(p => p === packSizeMode).length;

    // Dominant fulfillment
    const fulfillments = competitors.map(c => c.fulfillment).filter(Boolean);
    const fulfillmentMode = mode(fulfillments);
    const fulfillmentCount = fulfillments.filter(f => f === fulfillmentMode).length;

    // Average price
    const prices = competitors.map(c => parseFloat(c.price) || 0).filter(p => p > 0);
    const avgPrice = prices.length > 0 ? prices.reduce((a, b) => a + b, 0) / prices.length : 0;

    // Your position
    let position = 'N/A';
    let positionColor = 'text-gray-400';
    if (productRetailPrice > 0 && avgPrice > 0) {
      const priceDiff = ((productRetailPrice - avgPrice) / avgPrice) * 100;
      if (priceDiff < 0) {
        position = `${Math.abs(priceDiff).toFixed(0)}% below avg ✓`;
        positionColor = 'text-green-400';
      } else if (priceDiff > 0) {
        position = `${priceDiff.toFixed(0)}% above avg`;
        positionColor = priceDiff > 20 ? 'text-red-400' : 'text-yellow-400';
      } else {
        position = 'At avg price';
        positionColor = 'text-blue-400';
      }
    }

    return {
      packSize: packSizeMode ? `${packSizeMode} (${packSizeCount} of ${packSizes.length})` : 'N/A',
      fulfillment: fulfillmentMode ? `${fulfillmentMode} (${fulfillmentCount} of ${fulfillments.length})` : 'N/A',
      avgPrice: avgPrice > 0 ? `$${safeToFixed(avgPrice)}` : 'N/A',
      position,
      positionColor
    };
  }, [competitors, productRetailPrice]);

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return 'Unknown';
    }
  };

  // Empty state
  if (!hasData && !loading && competitors.length === 0) {
    return (
      <div className="space-y-4">
        {/* BSR Section - Always show at top */}
        <BSRSection productId={productId} asin={productData?.amazon_asin} />
        
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Trophy className="w-4 h-4 text-yellow-400" />
            <span className="text-sm font-semibold text-white">Competitive Analysis</span>
          </div>
        </div>
        
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="py-12">
            <div className="text-center">
              <BarChart3 className="w-16 h-16 mx-auto mb-4 text-gray-600" />
              <h3 className="text-lg font-medium text-white mb-2">No Competitive Data Yet</h3>
              <p className="text-sm text-gray-400 mb-6">
                Track your competitors to make smarter pricing decisions
              </p>
              <Button
                onClick={() => {
                  setHasData(true);
                  handleAddCompetitor();
                }}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="start-analysis-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Start Analysis
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* BSR Section - Your Product's Best Seller Rank */}
      <BSRSection productId={productId} asin={productData?.amazon_asin} />
      
      {/* BSR Trend Chart - Historical BSR data */}
      {productData?.amazon_asin && (
        <BSRTrendChart 
          productId={productId} 
          asin={productData?.amazon_asin}
          height={180}
        />
      )}
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Trophy className="w-4 h-4 text-yellow-400" />
          <span className="text-sm font-semibold text-white">Competitive Analysis</span>
          <Badge variant="outline" className="text-[10px] text-gray-400 ml-2">
            Last Updated: {formatDate(lastUpdated)}
          </Badge>
        </div>
        <Button
          variant="default"
          size="sm"
          onClick={handleSave}
          disabled={saving || !productId}
          className="h-7 text-xs bg-blue-600 hover:bg-blue-700"
          data-testid="save-competition-btn"
        >
          {saving ? (
            <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
          ) : (
            <Save className="w-3 h-3 mr-1" />
          )}
          Save
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      ) : (
        <>
          {/* Market Info Section */}
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-medium text-gray-400 uppercase tracking-wide flex items-center gap-2">
                <Package className="w-3 h-3" />
                Market Info
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="md:col-span-2">
                  <label className="text-[10px] text-gray-400 uppercase tracking-wide mb-1 block">
                    Amazon Category
                  </label>
                  <Input
                    type="text"
                    maxLength={200}
                    value={marketInfo.amazon_category}
                    onChange={(e) => setMarketInfo({ ...marketInfo, amazon_category: e.target.value })}
                    className="h-8 text-xs bg-gray-700 border-gray-600 text-white"
                    placeholder="e.g., Pet Supplies > Dogs > Treats"
                    data-testid="amazon-category-input"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-gray-400 uppercase tracking-wide mb-1 block">
                    Market Price Range
                  </label>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-400 text-xs">$</span>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={marketInfo.price_range_min || ''}
                      onChange={(e) => setMarketInfo({ ...marketInfo, price_range_min: parseFloat(e.target.value) || 0 })}
                      className="h-8 text-xs bg-gray-700 border-gray-600 text-white w-20"
                      placeholder="0.00"
                      data-testid="price-min-input"
                    />
                    <span className="text-gray-400 text-xs">to $</span>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={marketInfo.price_range_max || ''}
                      onChange={(e) => setMarketInfo({ ...marketInfo, price_range_max: parseFloat(e.target.value) || 0 })}
                      className="h-8 text-xs bg-gray-700 border-gray-600 text-white w-20"
                      placeholder="0.00"
                      data-testid="price-max-input"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-[10px] text-gray-400 uppercase tracking-wide mb-1 block">
                    Avg Reviews in Category
                  </label>
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    value={marketInfo.avg_reviews || ''}
                    onChange={(e) => setMarketInfo({ ...marketInfo, avg_reviews: parseInt(e.target.value) || 0 })}
                    className="h-8 text-xs bg-gray-700 border-gray-600 text-white w-32"
                    placeholder="0"
                    data-testid="avg-reviews-input"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Competitors Grid */}
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-xs font-medium text-gray-400 uppercase tracking-wide flex items-center gap-2">
                  <Star className="w-3 h-3" />
                  Top Competitors (up to 10)
                </CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleAddCompetitor}
                  disabled={competitors.length >= 10}
                  className="h-6 text-[10px]"
                  data-testid="add-competitor-btn"
                >
                  <Plus className="w-3 h-3 mr-1" />
                  Add Row
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {competitors.length === 0 ? (
                <div className="text-center py-6 text-gray-500">
                  <p className="text-sm">No competitors added yet</p>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleAddCompetitor}
                    className="mt-2 text-blue-400 hover:text-blue-300"
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Add Competitor
                  </Button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-gray-400 border-b border-gray-700">
                        <th className="text-left py-2 px-1 w-8">#</th>
                        <th className="text-left py-2 px-1">Brand/Seller</th>
                        <th className="text-left py-2 px-1">ASIN</th>
                        <th className="text-right py-2 px-1 w-24">Price</th>
                        <th className="text-left py-2 px-1">Pack Size</th>
                        <th className="text-center py-2 px-1">Fulfillment</th>
                        <th className="text-right py-2 px-1">Reviews</th>
                        <th className="text-right py-2 px-1 w-24">BSR</th>
                        <th className="text-center py-2 px-2 w-20">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700">
                      {competitors.map((competitor, index) => (
                        <tr key={index} className="hover:bg-gray-700/50">
                          <td className="py-2 px-1 text-gray-500 font-mono">{index + 1}</td>
                          <td className="py-2 px-1">
                            <Input
                              type="text"
                              maxLength={100}
                              value={competitor.brand}
                              onChange={(e) => handleCompetitorChange(index, 'brand', e.target.value)}
                              className="h-7 text-[11px] bg-gray-700 border-gray-600 text-white w-36"
                              placeholder="Brand name"
                              data-testid={`competitor-brand-${index}`}
                            />
                          </td>
                          <td className="py-2 px-1">
                            <div className="flex items-center gap-1">
                              <div className="relative">
                                <Input
                                  type="text"
                                  maxLength={10}
                                  value={competitor.asin}
                                  onChange={(e) => handleCompetitorChange(index, 'asin', e.target.value.toUpperCase())}
                                  onBlur={(e) => handleCompetitorAsinLookup(index, e.target.value)}
                                  className={`h-7 text-[11px] bg-gray-700 text-white font-mono w-28 ${
                                    competitor.asin && competitor.asin.length > 0 && competitor.asin.length !== 10
                                      ? 'border-amber-500 focus:border-amber-500'
                                      : 'border-gray-600'
                                  }`}
                                  placeholder="B0XXXXXXXX"
                                  data-testid={`competitor-asin-${index}`}
                                  disabled={loadingCompetitorRow === index}
                                />
                                {/* Show character count hint when typing incomplete ASIN */}
                                {competitor.asin && competitor.asin.length > 0 && competitor.asin.length !== 10 && (
                                  <span className="absolute -bottom-3 left-0 text-[9px] text-amber-400">
                                    {competitor.asin.length}/10 chars
                                  </span>
                                )}
                              </div>
                              {loadingCompetitorRow === index ? (
                                <Loader2 className="w-3 h-3 text-blue-400 animate-spin" />
                              ) : competitor.asin && competitor.asin.length === 10 ? (
                                <button
                                  type="button"
                                  onClick={() => handleRefreshCompetitor(index)}
                                  className="p-0.5 text-gray-400 hover:text-blue-400 transition-colors"
                                  title="Refresh competitor data from Amazon"
                                  data-testid={`competitor-refresh-${index}`}
                                >
                                  <RefreshCw className="w-3 h-3" />
                                </button>
                              ) : null}
                            </div>
                          </td>
                          <td className="py-2 px-1">
                            <div className="flex items-center">
                              <span className="text-gray-400 text-[10px] mr-1">$</span>
                              <Input
                                type="number"
                                min="0"
                                step="0.01"
                                value={competitor.price || ''}
                                onChange={(e) => handleCompetitorChange(index, 'price', parseFloat(e.target.value) || 0)}
                                className="h-7 text-[11px] bg-gray-700 border-gray-600 text-white w-[85px] min-w-[70px] text-right [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                placeholder="0.00"
                                data-testid={`competitor-price-${index}`}
                              />
                            </div>
                          </td>
                          <td className="py-2 px-1">
                            <Input
                              type="text"
                              maxLength={20}
                              value={competitor.pack_size}
                              onChange={(e) => handleCompetitorChange(index, 'pack_size', e.target.value)}
                              className="h-7 text-[11px] bg-gray-700 border-gray-600 text-white w-20"
                              placeholder="6-pack"
                              data-testid={`competitor-packsize-${index}`}
                            />
                          </td>
                          <td className="py-2 px-1">
                            <select
                              value={competitor.fulfillment}
                              onChange={(e) => handleCompetitorChange(index, 'fulfillment', e.target.value)}
                              className="h-7 text-[11px] bg-gray-700 border border-gray-600 text-white rounded px-1"
                              data-testid={`competitor-fulfillment-${index}`}
                            >
                              {FULFILLMENT_OPTIONS.map(opt => (
                                <option key={opt} value={opt}>{opt}</option>
                              ))}
                            </select>
                          </td>
                          <td className="py-2 px-1">
                            <Input
                              type="number"
                              min="0"
                              step="1"
                              value={competitor.reviews || ''}
                              onChange={(e) => handleCompetitorChange(index, 'reviews', parseInt(e.target.value) || 0)}
                              className="h-7 text-[11px] bg-gray-700 border-gray-600 text-white w-20 text-right [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                              placeholder="0"
                              data-testid={`competitor-reviews-${index}`}
                            />
                          </td>
                          <td className="py-2 px-1">
                            <Input
                              type="number"
                              min="0"
                              step="1"
                              value={competitor.bsr || ''}
                              onChange={(e) => handleCompetitorChange(index, 'bsr', parseInt(e.target.value) || 0)}
                              className="h-7 text-[11px] bg-gray-700 border-gray-600 text-white w-[90px] min-w-[80px] text-right [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                              placeholder="0"
                              data-testid={`competitor-bsr-${index}`}
                            />
                          </td>
                          <td className="py-2 px-2 text-center" style={{ minWidth: '70px' }}>
                            <div className="flex items-center justify-center gap-2">
                              <button
                                onClick={() => openUrlModal(index)}
                                className={`p-1.5 rounded transition-colors ${competitor.url ? 'text-blue-400 hover:text-blue-300 bg-blue-900/30' : 'text-gray-400 hover:text-gray-300 hover:bg-gray-700'}`}
                                title={competitor.url ? `View/Edit URL: ${competitor.url.substring(0, 30)}...` : 'Add URL'}
                                data-testid={`competitor-url-${index}`}
                              >
                                <Link2 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleRemoveCompetitor(index)}
                                className="p-1.5 rounded text-gray-400 hover:text-red-400 hover:bg-red-900/30 transition-colors"
                                data-testid={`delete-competitor-${index}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Insights */}
          {insights && (
            <Card className="bg-gradient-to-r from-blue-900/30 to-purple-900/30 border-blue-700/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-xs font-medium text-blue-400 uppercase tracking-wide flex items-center gap-2">
                  <BarChart3 className="w-3 h-3" />
                  Quick Insights (Auto-calculated)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="space-y-1">
                    <div className="flex items-center gap-1 text-[10px] text-gray-400">
                      <Package className="w-3 h-3" />
                      Most Common Pack Size
                    </div>
                    <p className="text-sm font-medium text-white">{insights.packSize}</p>
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center gap-1 text-[10px] text-gray-400">
                      <Truck className="w-3 h-3" />
                      Dominant Fulfillment
                    </div>
                    <p className="text-sm font-medium text-white">{insights.fulfillment}</p>
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center gap-1 text-[10px] text-gray-400">
                      <DollarSign className="w-3 h-3" />
                      Avg Competitor Price
                    </div>
                    <p className="text-sm font-medium text-white">{insights.avgPrice}</p>
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center gap-1 text-[10px] text-gray-400">
                      {insights.positionColor === 'text-green-400' ? (
                        <TrendingDown className="w-3 h-3" />
                      ) : (
                        <TrendingUp className="w-3 h-3" />
                      )}
                      Your Position
                    </div>
                    <p className={`text-sm font-medium ${insights.positionColor}`}>
                      {insights.position}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Notes Section */}
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-medium text-gray-400 uppercase tracking-wide flex items-center gap-2">
                <FileText className="w-3 h-3" />
                Notes &amp; Observations
              </CardTitle>
            </CardHeader>
            <CardContent>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                maxLength={2000}
                className="w-full min-h-[100px] p-3 text-sm bg-gray-700 border border-gray-600 text-white rounded-md resize-none placeholder:text-gray-500"
                placeholder="Add notes about competitor strategies, market trends, pricing observations..."
                data-testid="competition-notes"
              />
              <div className="text-right text-[10px] text-gray-500 mt-1">
                {notes.length}/2000
              </div>
            </CardContent>
          </Card>

          {/* Info Note */}
          <div className="flex items-start gap-2 p-2 bg-gray-800/50 rounded-lg border border-gray-700">
            <AlertCircle className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
            <p className="text-[10px] text-gray-400">
              Track competitor ASINs, pricing, and market positioning. Your Position compares your retail price (${safeToFixed(productRetailPrice)}) to the average competitor price.
            </p>
          </div>
        </>
      )}
      
      {/* URL Modal */}
      {urlModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-gray-800 rounded-lg border border-gray-700 shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between p-4 border-b border-gray-700">
              <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                <Link2 className="w-4 h-4 text-blue-400" />
                Product URL
              </h3>
              <button
                onClick={() => setUrlModalOpen(false)}
                className="p-1 text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              {urlModalIndex !== null && competitors[urlModalIndex] && (
                <div className="text-xs text-gray-400 space-y-1">
                  <div>
                    <span className="text-gray-500">Competitor:</span>{' '}
                    <span className="text-white">{competitors[urlModalIndex].brand || 'Unnamed'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">ASIN:</span>{' '}
                    <span className="text-white font-mono">{competitors[urlModalIndex].asin || 'N/A'}</span>
                  </div>
                </div>
              )}
              <div>
                <label className="text-[10px] text-gray-400 uppercase tracking-wide mb-1 block">
                  Amazon Product URL
                </label>
                <Input
                  type="url"
                  value={urlModalValue}
                  onChange={(e) => setUrlModalValue(e.target.value)}
                  className="h-9 text-xs bg-gray-700 border-gray-600 text-white"
                  placeholder="https://www.amazon.com/dp/B0XXXXXXXX"
                  data-testid="url-modal-input"
                />
                <p className="text-[10px] text-gray-500 mt-1">
                  Paste the full Amazon product URL
                </p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-700 bg-gray-800/50">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setUrlModalOpen(false)}
                className="h-8 text-xs text-gray-400 hover:text-white"
              >
                Cancel
              </Button>
              {urlModalValue && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openUrlInNewTab(urlModalValue)}
                  className="h-8 text-xs border-gray-600 text-blue-400 hover:text-blue-300"
                >
                  <ExternalLink className="w-3 h-3 mr-1" />
                  Open
                </Button>
              )}
              <Button
                size="sm"
                onClick={saveUrl}
                className="h-8 text-xs bg-blue-600 hover:bg-blue-500 text-white"
                data-testid="url-modal-save"
              >
                <Save className="w-3 h-3 mr-1" />
                Save
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompetitionTab;
